import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { TableColumns } from '@ff-it/ui';
import type { ActivityType } from 'types';
import type { OutletOrServiceCategoryProduct, ProductFormConfig } from 'modules/supplier/products';
import type { ProductAuthoringController } from 'modules/supplier/pricelist/ProductAuthoring/controller';
import type {
  ProductAuthoringCatalogActions,
  ProductAuthoringProduct,
} from 'modules/supplier/pricelist/ProductAuthoring/types';
import { rtbProductConfig } from 'modules/rtb';
import { oohProductConfig } from 'modules/ooh';
import { pressProductColumns, pressProductConfig } from 'modules/press';

/**
 * Composition root for activity-type-owned product surfaces.
 *
 * This application-level composition root is the only place allowed to import
 * every type module, so the binding of `ActivityType` to its config form,
 * product-index columns, and price-list authoring workspace lives here. Shared
 * modules under `modules/supplier` stay type-agnostic: they receive the
 * resolved pieces as props.
 */
export type ProductAuthoringWorkspaceProps = {
  controller: ProductAuthoringController;
  priceListEndpoint: string | null;
  canEdit?: boolean;
  catalogActions?: ProductAuthoringCatalogActions<ProductAuthoringProduct>;
};

/**
 * The registry is deliberately controller-agnostic. Each workspace owns its
 * controller contract; callers narrow it at the activity-type dispatch point.
 */
export type ProductTypeModule = {
  /** MEDIA configuration form rendered inside the product form's Config section. */
  formConfig?: ProductFormConfig;
  /** Extra product-index columns, composed between identity and detail columns. */
  columns?: TableColumns<OutletOrServiceCategoryProduct>;
  /**
   * Price-list authoring workspace dispatched on `PriceList.type`; absent
   * means the default scalar workspace.
   */
  workspace?:
    | ComponentType<ProductAuthoringWorkspaceProps>
    | LazyExoticComponent<ComponentType<ProductAuthoringWorkspaceProps>>;
};

// Keep type-owned authoring workspaces out of the product CRUD route's initial
// bundle. They are loaded only when the Prices route dispatches to that type.
const pressProductAuthoringWorkspace = lazy(() =>
  import('modules/press/PressCatalogue/PressProductAuthoringRoute').then(({ PressProductAuthoringRoute }) => ({
    default: PressProductAuthoringRoute,
  })),
);
const radioProductAuthoringWorkspace = lazy(() =>
  import('modules/radio/ProductAuthoring/RadioProductAuthoring').then(({ RadioProductAuthoring }) => ({
    default: RadioProductAuthoring,
  })),
);

const rtbModule: ProductTypeModule = { formConfig: rtbProductConfig };

const PRODUCT_TYPE_MODULES: Partial<Record<ActivityType, ProductTypeModule>> = {
  RTB: rtbModule,
  WEB: rtbModule,
  OOH: { formConfig: oohProductConfig },
  PRESS: {
    formConfig: pressProductConfig,
    columns: pressProductColumns,
    workspace: pressProductAuthoringWorkspace,
  },
  RADIO: { workspace: radioProductAuthoringWorkspace },
};

export function getProductTypeModule(type: ActivityType | null | undefined): ProductTypeModule | undefined {
  return (type && PRODUCT_TYPE_MODULES[type]) || undefined;
}
